// @ts-check
// Menu de ações das telas dos fluxos (três pontos da linha, "⋯" do editor, botão dividido das estatísticas): botão que
// abre um menu com itens de verdade (role="menuitem"), setas para andar, Enter para escolher, Esc para fechar e o foco
// de volta no botão. A superfície é o Dropdown do sistema (posição, clique fora).
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { Dropdown } from '../../ui/dropdown.js';
import { Icon } from '../../ui/icon.js';

/**
 * @typedef {{ key: string, text: string, icon?: string, onSelect: () => void, accent?: 'default' | 'danger', disabled?: boolean,
 *   separatorBefore?: boolean, hint?: string | null }} FlowMenuItem
 */

/**
 * @param {{ items: FlowMenuItem[], label?: string | null, icon?: string | null, ariaLabel: string, placement?: 'bottom-start' | 'bottom-end' | 'top-end',
 *   className?: string, width?: number, testId?: string, disabled?: boolean }} props
 */
export function FlowMenuButton({ items, label = null, icon = 'IconDotsVertical', ariaLabel, placement = 'bottom-end', className = 'flow-menu-trigger', width = 240, testId, disabled = false }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const listRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const visibleItems = items.filter(Boolean);

  const close = (/** @type {boolean} */ restoreFocus = true) => {
    setIsOpen(false);
    if (restoreFocus) requestAnimationFrame(() => anchorRef.current?.focus({ preventScroll: true }));
  };
  const focusItem = (/** @type {number} */ index) => {
    const buttons = /** @type {HTMLButtonElement[]} */ ([...(listRef.current?.querySelectorAll('[role="menuitem"]:not([disabled])') ?? [])]);
    if (buttons.length === 0) return;
    buttons[(index + buttons.length) % buttons.length]?.focus();
  };
  const open = () => {
    setIsOpen(true);
    requestAnimationFrame(() => focusItem(0));
  };
  /** @param {KeyboardEvent} event */
  const onMenuKeyDown = (event) => {
    const buttons = /** @type {HTMLButtonElement[]} */ ([...(listRef.current?.querySelectorAll('[role="menuitem"]:not([disabled])') ?? [])]);
    const index = buttons.indexOf(/** @type {HTMLButtonElement} */ (document.activeElement));
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusItem(index + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusItem(index - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusItem(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusItem(buttons.length - 1);
    } else if (event.key === 'Tab') {
      close(false);
    }
  };

  return html`<button type="button" ref=${anchorRef} class=${className} aria-haspopup="menu" aria-expanded=${isOpen ? 'true' : 'false'} aria-label=${ariaLabel}
      title=${label ? undefined : ariaLabel} disabled=${disabled} data-testid=${testId}
      onClick=${(/** @type {MouseEvent} */ event) => {
        event.stopPropagation();
        if (isOpen) close();
        else open();
      }}
      onKeyDown=${(/** @type {KeyboardEvent} */ event) => {
        if (event.key === 'ArrowDown' && !isOpen) {
          event.preventDefault();
          open();
        }
      }}>
      ${icon ? html`<${Icon} name=${icon} size=${16} />` : null}
      ${label ? html`<span>${label}</span>` : null}
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => close()} placement=${placement} width=${width} role="presentation">
      <div class="dropdown-menu-items flow-menu" role="menu" aria-label=${ariaLabel} ref=${listRef} onKeyDown=${onMenuKeyDown}>
        ${visibleItems.map((item) => html`${item.separatorBefore ? html`<div class="dropdown-menu-separator" role="separator"></div>` : null}
          <button type="button" key=${item.key} role="menuitem" class="menu-item flow-menu-item" data-accent=${item.accent ?? 'default'} disabled=${item.disabled}
            title=${item.hint ?? undefined} tabindex="-1"
            onClick=${(/** @type {MouseEvent} */ event) => {
              event.stopPropagation();
              close(false);
              item.onSelect();
            }}>
            <span class="menu-item-left">
              ${item.icon ? html`<${Icon} name=${item.icon} size=${16} stroke=${1.6} />` : null}
              <span class="menu-item-text">${item.text}</span>
            </span>
          </button>`)}
      </div>
    <//>`;
}
